import { ReactElement } from 'react';

// Developer-portal sidebar (rail) icons — the exact stroke-based set from the
// approved direction mockup. Kept dev-portal-local rather than added to the
// shared `Icon` component so this thin 24x24 stroke style stays scoped to the
// rail and never changes glyphs elsewhere in the app. Each icon inherits its
// color and opacity from the rail's SCSS (`.nav_item svg` / `.is_active svg`)
// via `stroke="currentColor"`, so the active-item gold and hover states apply
// without any per-icon styling here.

export type RailIconName =
    | 'home'
    | 'api_keys'
    | 'webhooks'
    | 'sandbox'
    | 'orders'
    | 'sales'
    | 'payments'
    | 'payouts'
    | 'gift_cards'
    | 'settings';

const PATHS: Record<RailIconName, ReactElement> = {
    home: <path d="M3 10.5 12 3l9 7.5M5 9.5V20h14V9.5" />,
    api_keys: (
        <>
            <circle cx="8" cy="15" r="4" />
            <path d="M11 12 20 3M17 6l2 2M14 9l2 2" />
        </>
    ),
    webhooks: <path d="M9 12a4 4 0 0 1 4-4h4M15 12a4 4 0 0 1-4 4H7" />,
    sandbox: <path d="M6 16a4 4 0 0 1 .9-7.9A5 5 0 0 1 17 7a4 4 0 0 1 1 7.9" />,
    orders: (
        <>
            <rect x="3" y="6" width="18" height="12" rx="2" />
            <path d="M3 10h18" />
        </>
    ),
    sales: <path d="M12 3v18M18 9 12 3 6 9" />,
    payments: (
        <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v10M9.5 9.5h4a1.8 1.8 0 0 1 0 3.5h-3a1.8 1.8 0 0 0 0 3.5h4" />
        </>
    ),
    payouts: <path d="M4 12 20 4l-6 16-3-7z" />,
    gift_cards: (
        <>
            <rect x="3" y="8" width="18" height="12" rx="2" />
            <path d="M3 12h18M12 8V6a2 2 0 0 1 4 0M12 8V6a2 2 0 0 0-4 0" />
        </>
    ),
    settings: (
        <>
            <circle cx="12" cy="12" r="3" />
            <path d="M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.4-2.3 1a7 7 0 0 0-1.7-1l-.3-2.5H9.4L9 4.6a7 7 0 0 0-1.7 1l-2.3-1-2 3.4 2 1.5a7 7 0 0 0 0 2l-2 1.5 2 3.4 2.3-1a7 7 0 0 0 1.7 1l.3 2.5h4.9l.3-2.5a7 7 0 0 0 1.7-1l2.3 1 2-3.4-2-1.5c.06-.3.1-.66.1-1Z" />
        </>
    )
};

export function RailIcon({ name }: { name: RailIconName }) {
    return (
        <svg
            width={17}
            height={17}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            aria-hidden="true">
            {PATHS[name]}
        </svg>
    );
}
